import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { Link } from "wouter";
import { ArrowLeft, Award, ChevronRight } from "lucide-react";

const STAGES = [
  {
    stage: "Foundation",
    credential: "Foundation Pharmacist Credential",
    desc: "Achieved during or shortly after the foundation pharmacist training year (FPY). Demonstrates readiness for independent, safe, and effective pharmacy practice.",
    evidence: [
      "Completion of foundation pharmacist training year (supervised)",
      "Demonstration against Foundation Pharmacy Framework outcomes",
      "Portfolio of practice evidence reviewed by designated supervisor",
    ],
    note: "Mandatory from 2026 for all newly qualified pharmacists completing the FPY.",
  },
  {
    stage: "Advanced",
    credential: "RPS Advanced Pharmacist Credential",
    desc: "Recognises advanced pharmacy practice. Based on the Advanced Pharmacy Practice Framework (APPF) four pillars. Typically attained at Band 7/8a.",
    evidence: [
      "Portfolio of evidence mapped to the APPF four pillars",
      "Demonstration of advanced clinical practice in specialist area",
      "Peer review and assessor interview with RPS Faculty assessors",
      "Supporting evidence of leadership, teaching, and research contributions",
    ],
    note: "Allows use of the credential 'RPS Advanced Pharmacist' and is a common requirement for Band 8a+ roles.",
  },
  {
    stage: "Consultant",
    credential: "RPS Consultant Pharmacist Credential",
    desc: "The highest level of RPS credentialing, recognising consultant-level practice across all four APPF pillars. Equivalent to the Consultant Pharmacist role (Band 8c/8d).",
    evidence: [
      "Extensive portfolio demonstrating consultant-level evidence across all four pillars",
      "Evidence of national/international contribution and reputation",
      "Research, publications, or equivalent scholarly output",
      "Senior assessor panel review",
    ],
    note: "Allows use of the title 'Consultant Pharmacist' and supports Band 8c/8d appointment.",
  },
];

export default function RpsCredentialing() {
  return (
    <PharmacistLayout title="RPS Credentialing Pathway">
      <div className="space-y-6 max-w-4xl">
        <Link href="/pharmacist/career">
          <button className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Career Pathways
          </button>
        </Link>

        <div className="bg-gradient-to-br from-violet-900 to-violet-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Award className="h-7 w-7 text-violet-300" />
            <div>
              <h2 className="text-xl font-bold">RPS Credentialing Pathway</h2>
              <p className="text-violet-300 text-sm">Royal Pharmaceutical Society · Foundation → Advanced → Consultant</p>
            </div>
          </div>
          <p className="text-violet-200 text-sm leading-relaxed">
            RPS Credentialing provides a nationally recognised framework for career progression from newly qualified pharmacist to consultant-level practice. Each stage has a defined portfolio process assessed by RPS Faculty.
          </p>
        </div>

        <div className="relative">
          <div className="absolute left-[22px] top-6 bottom-6 w-0.5 bg-violet-100 z-0" />
          <div className="space-y-6">
            {STAGES.map((s, i) => (
              <div key={s.stage} className="relative flex gap-5">
                <div className="shrink-0 h-11 w-11 rounded-full bg-violet-700 text-white font-bold text-sm flex items-center justify-center z-10 shadow-md">
                  {i + 1}
                </div>
                <div className="flex-1 bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                  <div className="flex items-start gap-2 mb-2">
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wider text-violet-600">{s.stage}</span>
                      <h3 className="font-bold text-slate-900 mt-0.5">{s.credential}</h3>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-3">{s.desc}</p>
                  <div className="bg-slate-50 rounded-xl p-4 mb-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Evidence requirements</p>
                    <ul className="space-y-1.5">
                      {s.evidence.map((e, j) => (
                        <li key={j} className="flex items-start gap-2 text-xs text-slate-600">
                          <ChevronRight className="h-3.5 w-3.5 text-violet-500 shrink-0 mt-0.5" />
                          {e}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-xs text-violet-700 bg-violet-50 rounded-lg px-3 py-2 border border-violet-200">{s.note}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h4 className="font-bold text-slate-900 mb-2">Getting Started with RPS Credentialing</h4>
          <ol className="space-y-2">
            {[
              "Ensure you have RPS membership (MRPharmS) — required for credentialing",
              "Use the APPF tracker in your RPS Faculty portfolio to map your evidence",
              "Work with a mentor or RPS development adviser to identify evidence gaps",
              "Register for the relevant credentialing assessment when you feel ready",
              "Submit your portfolio and attend the assessor interview",
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                <span className="shrink-0 h-5 w-5 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                {step}
              </li>
            ))}
          </ol>
          <a
            href="https://www.rpharms.com/development/rps-faculty"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 mt-4 text-sm text-violet-600 hover:text-violet-800 font-medium"
          >
            Visit RPS Faculty website →
          </a>
        </div>

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          Information is based on the RPS credentialing framework as at 2025. Processes and requirements may change — always refer to the RPS Faculty website for the most current guidance.
        </div>
      </div>
    </PharmacistLayout>
  );
}
